import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  mallApi,
  type MallAddressInput,
  type MallCatalogParams,
  type MallCheckoutRequest,
  type MallOrderParams,
} from '@/api/mall'
import { getApiErrorMessage } from '@/utils/apiError'

export const mallKeys = {
  all: ['mall'] as const,
  catalogRoot: () => ['mall', 'catalog'] as const,
  catalog: (params: MallCatalogParams) => ['mall', 'catalog', params] as const,
  product: (productId: number) => ['mall', 'product', productId] as const,
  cart: () => ['mall', 'cart'] as const,
  addresses: () => ['mall', 'addresses'] as const,
  orders: () => ['mall', 'orders'] as const,
  order: (orderId: number) => ['mall', 'orders', orderId] as const,
}

export function useMallCatalog(params: MallCatalogParams) {
  return useQuery({
    queryKey: mallKeys.catalog(params),
    queryFn: async () => (await mallApi.getCatalog(params)).data.data,
    placeholderData: (previousData) => previousData,
  })
}

export function useMallProduct(productId: number) {
  return useQuery({
    queryKey: mallKeys.product(productId),
    queryFn: async () => (await mallApi.getProduct(productId)).data.data,
    enabled: Number.isFinite(productId) && productId > 0,
  })
}

export function useMallCart(enabled = true) {
  return useQuery({
    queryKey: mallKeys.cart(),
    queryFn: async () => (await mallApi.getCart()).data.data,
    enabled,
  })
}

export function useMallAddresses(enabled = true) {
  return useQuery({
    queryKey: mallKeys.addresses(),
    queryFn: async () => (await mallApi.getAddresses()).data.data ?? [],
    enabled,
  })
}

export function useMallOrders(params: MallOrderParams) {
  return useQuery({
    queryKey: [...mallKeys.orders(), params],
    queryFn: async () => (await mallApi.getOrders(params)).data.data,
    placeholderData: (previousData) => previousData,
  })
}

export function useMallOrder(orderId: number) {
  return useQuery({
    queryKey: mallKeys.order(orderId),
    queryFn: async () => (await mallApi.getOrder(orderId)).data.data,
    enabled: Number.isFinite(orderId) && orderId > 0,
  })
}

function useMallMutationError() {
  const { t } = useTranslation(['mall', 'common'])
  return (error: unknown) => toast.error(getApiErrorMessage(error, t))
}

export function useAddMallCartItem() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (body: { skuId: number; quantity: number }) => mallApi.addCartItem(body),
    onSuccess: (response) => {
      queryClient.setQueryData(mallKeys.cart(), response.data.data)
      toast.success(t('toast.addedToCart'))
    },
    onError,
  })
}

export function useUpdateMallCartItem() {
  const queryClient = useQueryClient()
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: number; quantity: number }) =>
      mallApi.updateCartItem(itemId, { quantity }),
    onSuccess: (response) => queryClient.setQueryData(mallKeys.cart(), response.data.data),
    onError,
  })
}

export function useDeleteMallCartItem() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (itemId: number) => mallApi.deleteCartItem(itemId),
    onSuccess: (response) => {
      queryClient.setQueryData(mallKeys.cart(), response.data.data)
      toast.success(t('toast.removedFromCart'))
    },
    onError,
  })
}

export function useClearMallCart() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: () => mallApi.clearCart(),
    onSuccess: (response) => {
      queryClient.setQueryData(mallKeys.cart(), response.data.data)
      toast.success(t('toast.cartCleared'))
    },
    onError,
  })
}

export function useCreateMallAddress() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (body: MallAddressInput) => mallApi.createAddress(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mallKeys.addresses() })
      toast.success(t('toast.addressSaved'))
    },
    onError,
  })
}

export function useUpdateMallAddress() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: ({ addressId, body }: { addressId: number; body: MallAddressInput }) =>
      mallApi.updateAddress(addressId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mallKeys.addresses() })
      toast.success(t('toast.addressSaved'))
    },
    onError,
  })
}

export function useDeleteMallAddress() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (addressId: number) => mallApi.deleteAddress(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mallKeys.addresses() })
      toast.success(t('toast.addressDeleted'))
    },
    onError,
  })
}

export function useSetDefaultMallAddress() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (addressId: number) => mallApi.setDefaultAddress(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mallKeys.addresses() })
      toast.success(t('toast.defaultAddressChanged'))
    },
    onError,
  })
}

export function useMallCheckout() {
  const queryClient = useQueryClient()
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (body: MallCheckoutRequest) => mallApi.checkout(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mallKeys.cart() })
      queryClient.invalidateQueries({ queryKey: mallKeys.orders() })
    },
    onError,
  })
}

export function useCancelMallOrder(orderId: number) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('mall')
  const onError = useMallMutationError()
  return useMutation({
    mutationFn: (reason: string) => mallApi.cancelOrder(orderId, { reason }),
    onSuccess: (response) => {
      queryClient.setQueryData(mallKeys.order(orderId), response.data.data)
      queryClient.invalidateQueries({ queryKey: mallKeys.orders() })
      toast.success(t('toast.orderCancelled'))
    },
    onError,
  })
}
